/**
 * Boss Fight - The Overfitter Confrontation
 * 3-phase boss battle where player must prove generalization beats memorization
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { audioEngine } from '../systems/AudioEngine.js';

export class BossFight {
    constructor(scene, player, regressionLine, overfitter) {
        this.scene = scene;
        this.player = player;
        this.regressionLine = regressionLine;
        this.overfitter = overfitter;

        // Boss state
        this.active = false;
        this.phase = 0; // 0 = not started, 1-3 = phases
        this.phaseTime = 0;
        // Different duration for each phase (escalating)
        this.phaseDurations = {
            1: CONSTANTS.BOSS_PHASE1_DURATION,
            2: CONSTANTS.BOSS_PHASE2_DURATION,
            3: CONSTANTS.BOSS_PHASE3_DURATION
        };

        // Boss health (damaged by staying on line)
        this.maxHealth = CONSTANTS.BOSS_MAX_HEALTH;
        this.health = this.maxHealth;

        // Attack patterns
        this.projectiles = [];
        this.attackTimer = 0;
        this.attackInterval = CONSTANTS.BOSS_ATTACK_INTERVAL_P1;

        // Weak spots (Phase 2)
        this.weakSpots = [];
        this.weakSpotTimer = 0;

        // Phase 3 - dual overfitters
        this.rightOverfitter = null;
        this.leftX = 0;
        this.rightX = 0;

        // Line stabilization at phase transitions
        this.phaseStartSlope = 0;
        this.phaseStartIntercept = this.scene.scale.height / 2;
        this.lineStabilizeTime = 0;

        // UI elements
        this.healthBar = null;
        this.phaseText = null;
        this.bossNameText = null;

        // Graphics for projectiles
        this.graphics = scene.add.graphics();
        this.graphics.setDepth(55);
    }

    start() {
        this.active = true;
        this.phase = 1; // Start at Phase 1
        this.phaseTime = 0;
        this.health = this.maxHealth;

        // Phase 1 has static line, no lerp change needed yet

        // Create boss UI
        this.createBossUI();

        // Show boss intro
        this.showPhaseIntro('PHASE 1: DATA BARRAGE');

        // Make overfitter bigger but position it far left
        this.overfitter.baseScale = CONSTANTS.OVERFITTER_SCALE;
        this.overfitter.sprite.setScale(this.overfitter.baseScale);

        // Screen shake
        this.scene.cameras.main.shake(500, 0.02);

        // Boss entrance sounds
        audioEngine.playOverfitterGrowl();
        audioEngine.playBossTransition();
    }

    createBossUI() {
        const centerX = this.scene.scale.width / 2;

        // Boss name
        this.bossNameText = this.scene.add.text(centerX, 50, 'THE OVERFITTER', {
            fontSize: '28px',
            fontFamily: 'Courier New, monospace',
            color: '#FF0000',
            fontStyle: 'bold'
        }).setOrigin(0.5).setScrollFactor(0).setDepth(200);

        // Health bar background
        this.healthBarBg = this.scene.add.rectangle(centerX, 80, 304, 24, 0x333333)
            .setScrollFactor(0).setDepth(200);

        // Health bar
        this.healthBar = this.scene.add.rectangle(centerX, 80, 300, 20, 0xFF0000)
            .setScrollFactor(0).setDepth(201);

        // Phase text
        this.phaseText = this.scene.add.text(centerX, 110, '', {
            fontSize: '16px',
            fontFamily: 'Courier New, monospace',
            color: '#FFAA00'
        }).setOrigin(0.5).setScrollFactor(0).setDepth(200);
    }

    showPhaseIntro(text) {
        this.phaseText.setText(text);
        this.phaseText.setAlpha(1);

        // Flash effect
        this.scene.tweens.add({
            targets: this.phaseText,
            alpha: 0.3,
            duration: 300,
            yoyo: true,
            repeat: 3
        });
    }

    update(delta, cameraX) {
        if (!this.active) return { defeated: false, victory: false };

        this.phaseTime += delta;

        // Get current phase duration
        const currentPhaseDuration = this.phaseDurations[this.phase] || 25000;

        // Health drains from TWO sources:
        // 1. Time passing (auto-drain) - guarantees phase ends when time runs out
        const timeDamage = (delta / currentPhaseDuration) * (this.maxHealth / 3); // Drain 1/3 of max health per phase
        this.health -= timeDamage;

        // 2. Staying on line (bonus damage) - rewards good play
        if (this.player.isOnLine && !this.player.isDead) {
            const bonusDamage = CONSTANTS.BOSS_DAMAGE_RATE * delta / 16;
            this.health -= bonusDamage;
        }

        // Update based on current phase
        switch (this.phase) {
            case 1:
                this.updatePhase1(delta, cameraX);
                break;
            case 2:
                this.updatePhase2(delta, cameraX);
                break;
            case 3:
                this.updatePhase3(delta, cameraX);
                break;
        }

        // Update projectiles
        this.updateProjectiles(delta, cameraX);

        // Update UI
        this.updateUI();

        // Check phase transitions when health drops to certain thresholds
        // Phase 1 ends at 66% health, Phase 2 ends at 33% health, Phase 3 ends at 0%
        const healthPercent = this.health / this.maxHealth;
        if (this.phase === 1 && healthPercent <= 0.66) {
            this.nextPhase();
        } else if (this.phase === 2 && healthPercent <= 0.33) {
            this.nextPhase();
        }

        // Check victory/defeat
        // Victory when boss health depleted
        if (this.health <= 0) {
            this.health = 0;
            return { defeated: false, victory: true };
        }

        if (this.player.isDead) {
            return { defeated: true, victory: false };
        }

        return { defeated: false, victory: false };
    }

    updatePhase1(delta, cameraX) {
        // Data Barrage - shoot data points at player
        this.attackTimer += delta;

        if (this.attackTimer >= this.attackInterval) {
            this.attackTimer = 0;
            this.fireProjectile(cameraX);
        }

        // Keep regression line STABLE during Phase 1 (flat, centered)
        // So player can focus on dodging projectiles
        this.regressionLine.targetSlope = 0;
        this.regressionLine.targetIntercept = this.scene.scale.height / 2;

        // Position overfitter at very far left (edge of screen)
        this.overfitter.x = cameraX + 20;
        this.overfitter.sprite.setPosition(this.overfitter.x, this.scene.scale.height / 2);

        // Overfitter pulses menacingly
        const pulse = 1 + Math.sin(this.phaseTime * 0.005) * 0.1;
        this.overfitter.sprite.setScale(this.overfitter.baseScale * pulse);
    }

    updatePhase2(delta, cameraX) {
        // Phase 2: Same as Phase 1 but FASTER + LINE MOVES
        this.attackTimer += delta;

        // Decrement stabilization time
        if (this.lineStabilizeTime > 0) {
            this.lineStabilizeTime -= delta;
            // During stabilization: keep line at starting position
            this.regressionLine.targetSlope = this.phaseStartSlope;
            this.regressionLine.targetIntercept = this.phaseStartIntercept;
        } else {
            // After stabilization: line moves around
            const lineMovement = Math.sin(this.phaseTime * 0.003) * CONSTANTS.BOSS_LINE_SLOPE_AMPLITUDE;
            const lineOffset = Math.sin(this.phaseTime * 0.002) * CONSTANTS.BOSS_LINE_OFFSET_AMPLITUDE;

            this.regressionLine.targetSlope = lineMovement;
            this.regressionLine.targetIntercept = this.scene.scale.height / 2 + lineOffset;
        }

        // Position overfitter at very far left (edge of screen)
        this.overfitter.x = cameraX + 20;
        this.overfitter.sprite.setPosition(this.overfitter.x, this.scene.scale.height / 2);

        // Overfitter pulses more aggressively
        const pulse = 1 + Math.sin(this.phaseTime * 0.008) * 0.15;
        this.overfitter.sprite.setScale(this.overfitter.baseScale * pulse);

        // FASTER attacks
        if (this.attackTimer >= CONSTANTS.BOSS_ATTACK_INTERVAL_P2) {
            this.attackTimer = 0;
            this.fireProjectile(cameraX);
        }
    }

    updatePhase3(delta, cameraX) {
        // PINCER ATTACK - Two overfitters from both sides!
        this.attackTimer += delta;

        // Create right overfitter if not exists
        if (!this.rightOverfitter) {
            this.rightOverfitter = this.scene.add.image(
                cameraX + this.scene.scale.width - 20,
                this.scene.scale.height / 2,
                'overfitter'
            );
            this.rightOverfitter.setDepth(100);
            this.rightOverfitter.setScale(this.overfitter.baseScale);
            this.rightOverfitter.setFlipX(true); // Mirror image - facing left

            // Starting positions - at the very edges
            this.leftX = cameraX + 20;
            this.rightX = cameraX + this.scene.scale.width - 20;
        }

        // Both move toward center, stop at configured limits
        const leftLimit = cameraX + this.scene.scale.width * CONSTANTS.BOSS_LEFT_LIMIT;
        const rightLimit = cameraX + this.scene.scale.width * CONSTANTS.BOSS_RIGHT_LIMIT;

        // Left overfitter moves right (toward center)
        if (this.leftX < leftLimit) {
            this.leftX += delta * 0.05;
        }

        // Right overfitter moves left (toward center)
        if (this.rightX > rightLimit) {
            this.rightX -= delta * 0.05;
        }

        // Update positions
        this.overfitter.x = this.leftX;
        this.overfitter.sprite.setPosition(this.leftX, this.scene.scale.height / 2);
        this.rightOverfitter.setPosition(this.rightX, this.scene.scale.height / 2);

        // Player at exact center
        this.player.x = cameraX + this.scene.scale.width * CONSTANTS.BOSS_PLAYER_CENTER;
        this.player.sprite.x = this.player.x;
        this.player.syncQuantumGlow(); // Keep quantum glow synced after forced position

        // Pulse effect for both
        const pulse = 1 + Math.sin(this.phaseTime * 0.008) * 0.15;
        this.overfitter.sprite.setScale(this.overfitter.baseScale * pulse);
        this.rightOverfitter.setScale(this.overfitter.baseScale * pulse);

        // Line movement with stabilization
        if (this.lineStabilizeTime > 0) {
            this.lineStabilizeTime -= delta;
            // During stabilization: keep line at starting position
            this.regressionLine.targetSlope = this.phaseStartSlope;
            this.regressionLine.targetIntercept = this.phaseStartIntercept;
        } else {
            // After stabilization: line moves (like Phase 2)
            const lineMovement = Math.sin(this.phaseTime * 0.003) * CONSTANTS.BOSS_LINE_SLOPE_AMPLITUDE;
            const lineOffset = Math.sin(this.phaseTime * 0.002) * CONSTANTS.BOSS_LINE_OFFSET_AMPLITUDE;
            this.regressionLine.targetSlope = lineMovement;
            this.regressionLine.targetIntercept = this.scene.scale.height / 2 + lineOffset;
        }

        // RAPID FIRE from BOTH sides!
        if (this.attackTimer >= CONSTANTS.BOSS_ATTACK_INTERVAL_P3) {
            this.attackTimer = 0;
            this.fireProjectileFromLeft(cameraX);
            this.fireProjectileFromRight(cameraX);
        }
    }

    fireProjectileFromLeft(cameraX) {
        // Fire from left overfitter - with randomization
        const startX = this.leftX + 50;
        const startY = this.scene.scale.height / 2 + (Math.random() - 0.5) * 100;

        // Aim at player with random offset
        const targetX = this.player.x;
        const targetY = this.player.y + (Math.random() - 0.5) * 150; // Random Y offset
        const baseAngle = Math.atan2(targetY - startY, targetX - startX);
        const angle = baseAngle + (Math.random() - 0.5) * 0.3; // Random angle spread
        const speed = CONSTANTS.BOSS_PROJECTILE_SPEED_MIN + Math.random() * (CONSTANTS.BOSS_PROJECTILE_SPEED_MAX - CONSTANTS.BOSS_PROJECTILE_SPEED_MIN) * 0.5;

        this.projectiles.push({
            x: startX,
            y: startY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            baseRadius: 8,
            radius: 8,
            maxRadius: 25,
            life: 4000,
            maxLife: 4000
        });
    }

    fireProjectileFromRight(cameraX) {
        // Fire from right overfitter - DIFFERENT STYLE with randomization
        const startX = this.rightX - 50;
        const startY = this.scene.scale.height / 2 + (Math.random() - 0.5) * 100;

        // Aim at player with random offset
        const targetX = this.player.x;
        const targetY = this.player.y + (Math.random() - 0.5) * 150; // Random Y offset
        const baseAngle = Math.atan2(targetY - startY, targetX - startX);
        const angle = baseAngle + (Math.random() - 0.5) * 0.3; // Random angle spread
        const speed = CONSTANTS.BOSS_PROJECTILE_SPEED + Math.random() * (CONSTANTS.BOSS_PROJECTILE_SPEED_MAX - CONSTANTS.BOSS_PROJECTILE_SPEED);

        this.projectiles.push({
            x: startX,
            y: startY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            baseRadius: 25,
            radius: 25,
            maxRadius: 10,
            life: 3000,
            maxLife: 3000,
            fromRight: true
        });
    }

    fireProjectile(cameraX) {
        // Fire from overfitter's right edge (mouth area)
        const overfitterWidth = this.overfitter.sprite.displayWidth / 2;
        const startX = this.overfitter.x + overfitterWidth;
        const startY = this.overfitter.sprite.y + (Math.random() - 0.5) * 50;

        // Aim toward player's CURRENT position
        const targetX = this.player.x;
        const targetY = this.player.y;

        const angle = Math.atan2(targetY - startY, targetX - startX);
        const speed = CONSTANTS.BOSS_PROJECTILE_SPEED;

        this.projectiles.push({
            x: startX,
            y: startY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            baseRadius: CONSTANTS.BOSS_PROJECTILE_BASE_RADIUS,
            radius: CONSTANTS.BOSS_PROJECTILE_BASE_RADIUS,
            maxRadius: CONSTANTS.BOSS_PROJECTILE_MAX_RADIUS,
            life: CONSTANTS.BOSS_PROJECTILE_LIFE,
            maxLife: CONSTANTS.BOSS_PROJECTILE_LIFE
        });

        // Sound effect
        audioEngine.playProjectileFire();
    }

    updateProjectiles(delta, cameraX) {
        this.graphics.clear();

        const dt = delta / 1000;

        this.projectiles = this.projectiles.filter(p => {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.life -= delta;

            // Size change over time
            const lifeProgress = 1 - (p.life / p.maxLife); // 0 to 1
            if (p.fromRight) {
                // Right projectiles SHRINK (start big, get small)
                p.radius = p.baseRadius - (p.baseRadius - p.maxRadius) * lifeProgress;
            } else {
                // Left projectiles GROW (start small, get big)
                p.radius = p.baseRadius + (p.maxRadius - p.baseRadius) * lifeProgress;
            }

            // Check collision with player
            const dx = p.x - this.player.x;
            const dy = p.y - this.player.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < p.radius + this.player.getCollisionRadius()) {
                // Check if quantum superposition can absorb the hit
                if (this.player.tryQuantumDodge()) {
                    // Quantum absorbed the hit! Remove projectile, no damage.
                    return false;
                }

                // Hit player - they grow (overfit!) AND take error damage
                this.player.grow(CONSTANTS.BOSS_PROJECTILE_HIT_GROWTH);
                this.scene.errorLevel += CONSTANTS.ERROR_DATAPOINT_HIT; // Same as normal play
                this.scene.cameras.main.shake(100, 0.01);
                this.scene.cameras.main.flash(50, 255, 100, 100); // Red flash
                audioEngine.playDamage();
                return false; // Remove projectile
            }

            // Remove if off screen or expired
            if (p.x > cameraX + this.scene.scale.width + 50 || p.life <= 0) {
                return false;
            }

            // Draw projectile - different colors for left vs right
            const intensity = 0.6 + lifeProgress * 0.4;

            if (p.fromRight) {
                // RIGHT: Purple/blue, electric look
                this.graphics.fillStyle(0x4400FF, 0.2 * intensity);
                this.graphics.fillCircle(p.x, p.y, p.radius * 1.8);

                this.graphics.fillStyle(0x6622FF, 0.4 * intensity);
                this.graphics.fillCircle(p.x, p.y, p.radius * 1.3);

                this.graphics.fillStyle(0x8844FF, intensity);
                this.graphics.fillCircle(p.x, p.y, p.radius);

                this.graphics.fillStyle(0xCCAAFF, 0.8);
                this.graphics.fillCircle(p.x, p.y, p.radius * 0.4);
            } else {
                // LEFT: Red/orange, fire look
                this.graphics.fillStyle(0xFF0000, 0.2 * intensity);
                this.graphics.fillCircle(p.x, p.y, p.radius * 1.8);

                this.graphics.fillStyle(0xFF2200, 0.4 * intensity);
                this.graphics.fillCircle(p.x, p.y, p.radius * 1.3);

                this.graphics.fillStyle(0xFF4444, intensity);
                this.graphics.fillCircle(p.x, p.y, p.radius);

                this.graphics.fillStyle(0xFFAAAA, 0.8);
                this.graphics.fillCircle(p.x, p.y, p.radius * 0.4);
            }

            return true;
        });
    }

    spawnWeakSpot(cameraX) {
        const x = cameraX + 200 + Math.random() * 100;
        const y = this.regressionLine.getYAtX(x); // On the line!

        this.weakSpots.push({
            x: x,
            y: y,
            radius: 25,
            life: 4000,
            hit: false
        });
    }

    updateWeakSpots(delta, cameraX) {
        this.weakSpots = this.weakSpots.filter(ws => {
            ws.life -= delta;

            // Check if player hits weak spot (must be on line near it)
            const dx = ws.x - this.player.x;
            const dy = ws.y - this.player.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < ws.radius + this.player.getCollisionRadius() && this.player.isOnLine) {
                // Hit weak spot - bonus damage to boss!
                this.health -= 10;
                ws.hit = true;
                this.scene.cameras.main.flash(100, 255, 255, 0);
                return false;
            }

            if (ws.life <= 0) return false;

            // Draw weak spot
            const pulse = Math.sin(ws.life * 0.01) * 0.3 + 0.7;
            this.graphics.fillStyle(0xFFFF00, pulse * 0.8);
            this.graphics.fillCircle(ws.x, ws.y, ws.radius);
            this.graphics.lineStyle(3, 0xFFAA00, pulse);
            this.graphics.strokeCircle(ws.x, ws.y, ws.radius);

            return true;
        });
    }

    nextPhase() {
        this.phase++;
        this.phaseTime = 0;
        this.attackTimer = 0;

        // Reset error to half at phase transition
        this.scene.errorLevel = Math.floor(this.scene.errorLevel / 2);

        // Keep line stable at transition - remember starting position
        this.phaseStartSlope = this.regressionLine.slope;
        this.phaseStartIntercept = this.regressionLine.intercept;
        this.lineStabilizeTime = 1500; // 1.5 seconds of stable line at phase start

        if (this.phase === 2) {
            this.showPhaseIntro('PHASE 2: LINE CHAOS');
            this.attackInterval = CONSTANTS.BOSS_ATTACK_INTERVAL_P2;
            this.regressionLine.lerpRate = CONSTANTS.BOSS_LINE_LERP_RATE_P2;
            audioEngine.playBossTransition();
        } else if (this.phase === 3) {
            this.showPhaseIntro('PHASE 3: PINCER ATTACK');
            this.attackInterval = CONSTANTS.BOSS_ATTACK_INTERVAL_P3;
            this.regressionLine.lerpRate = CONSTANTS.BOSS_LINE_LERP_RATE_P3;
            audioEngine.playBossTransition();
            audioEngine.playOverfitterGrowl();
        }

        this.scene.cameras.main.shake(300, 0.015);
    }

    updateUI() {
        if (!this.healthBar) return;

        // Update health bar
        const healthPercent = Math.max(0, this.health / this.maxHealth);
        this.healthBar.setScale(healthPercent, 1);
        this.healthBar.setX(this.scene.scale.width / 2 - (1 - healthPercent) * 150);

        // Color based on health
        if (healthPercent > 0.5) {
            this.healthBar.setFillStyle(0xFF0000);
        } else if (healthPercent > 0.25) {
            this.healthBar.setFillStyle(0xFF6600);
        } else {
            this.healthBar.setFillStyle(0xFFFF00);
        }
    }

    isActive() {
        return this.active;
    }

    destroy() {
        // Restore normal lerp rate
        this.regressionLine.lerpRate = CONSTANTS.LINE_LERP_RATE;

        if (this.graphics) this.graphics.destroy();
        if (this.healthBar) this.healthBar.destroy();
        if (this.healthBarBg) this.healthBarBg.destroy();
        if (this.bossNameText) this.bossNameText.destroy();
        if (this.phaseText) this.phaseText.destroy();
        if (this.rightOverfitter) this.rightOverfitter.destroy();
    }
}
